import { atom, type createStore } from 'jotai/vanilla';

export const AWARD_DESCRIPTIONS = {
	BOW: 'This award recognizes the top-rated vendors in the annual Best of Weddings list based on couple reviews.',
	HOF: 'The Hall of Fame recognizes vendors who have won Best of Weddings for 4+ years.',
} as const;

export interface Award {
	id: string;
	key: string; // original award key, e.g. "BOW2024", "BOW2008-09", "BOWHOF"
	type: 'BOW' | 'HOF';
	year: number | null; // null for HOF; for range keys (e.g. 2008-09) uses start year — for sorting only
	displayYear: string | null; // null for HOF; year string as-written in the key, e.g. "2024", "2008-09"
	label: string; // e.g. "2024 Best of Weddings Winner"
	description: string; // enriched copy from AWARD_DESCRIPTIONS
}

const BOW_KEY_REGEX = /^BOW(\d{4}(?:-\d{2})?)$/;

export const parseAwards = (awardKeys: string[]): Award[] =>
	(awardKeys ?? [])
		.map((key): Award | null => {
			if (key === 'BOWHOF') {
				return {
					id: key,
					key,
					type: 'HOF',
					year: null,
					displayYear: null,
					label: 'Hall of Fame',
					description: AWARD_DESCRIPTIONS.HOF,
				};
			}
			const match = key.match(BOW_KEY_REGEX);
			if (match) {
				const yearStr = match[1]; // "2024" or "2008-09"
				const rangeMatch = yearStr.match(/^(\d{4})-(\d{2})$/);
				let numericYear: number;
				let displayYearStr: string;
				if (rangeMatch) {
					// Range key e.g. "2008-09": use end year (2009) to match official badge
					// metadata and avoid collision with BOW2008.
					const century =
						Math.floor(Number.parseInt(rangeMatch[1], 10) / 100) * 100;
					numericYear = century + Number.parseInt(rangeMatch[2], 10);
					displayYearStr = String(numericYear);
				} else {
					numericYear = Number.parseInt(yearStr, 10);
					displayYearStr = yearStr;
				}
				return {
					id: key,
					key,
					type: 'BOW',
					year: numericYear,
					displayYear: displayYearStr,
					label: `${displayYearStr} Best of Weddings Winner`,
					description: AWARD_DESCRIPTIONS.BOW,
				};
			}
			return null; // unrecognised key — silently dropped
		})
		.filter((award): award is Award => award !== null)
		.sort((a, b) => {
			if (a.year === null && b.year === null) return 0;
			if (a.year === null) return 1; // HOF sinks to end
			if (b.year === null) return -1;
			return b.year - a.year; // BOW descending by year
		});

export const vendorAwardsAtom = atom<Award[]>([]);

export const setAwardsSummaryData = (
	store: ReturnType<typeof createStore>,
	awardKeys: string[],
) => {
	store.set(vendorAwardsAtom, parseAwards(awardKeys));
};

export const getAwardsSummaryDataToHydrate = (
	store: ReturnType<typeof createStore>,
) => ({
	awards: store.get(vendorAwardsAtom),
});

export const getAwardsSummaryInitialValues = (
	values: ReturnType<typeof getAwardsSummaryDataToHydrate>,
): Array<[typeof vendorAwardsAtom, Award[]]> => {
	return [[vendorAwardsAtom, values?.awards ?? []]];
};
